<template>
  <div class="row">
    <div class="col-12">
      <div class="rect rect--highlight mb-4">
        <StatisticsTimeSelector
          :lastValues="lastValues"
          :isEnabled="isEnabled"
          @update:currentPeriod="setCurrentPeriod"
        />
      </div>
      <AnalyticsTable :config="llmStatisticsConfig" :cells="getLLMStats" />
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { LLMObject } from '../../../../../../common/types/llm-type'
import Autocomplete from '@/components/helpers/Autocomplete.vue'
import Dropdown from '@/components/helpers/Dropdown.vue'
import Tooltip from '@/components/helpers/Tooltip.vue'
import { AnalyticsTableConfig } from '@/models/analytics-table.interface'
import { fromMillisecondsToSeconds } from '@/components/filters/number.filters'
import { LlmsService } from '@/services/bots/llms.services'
import AnalyticsTable from '@/components/analytics/AnalyticsTable.vue'
import StatisticsTimeSelector from '@/components/helpers/StatisticsTimeSelector.vue'
import { formatLocaleDate } from '@common/helpers/date.helper'

@Component({
  components: {
    Autocomplete,
    Dropdown,
    Tooltip,
    AnalyticsTable,
    StatisticsTimeSelector
  }
})
export default class LLMStatistics extends Vue {
  @Prop() LLM: LLMObject
  @Prop() variables: string[]

  private llmService: LlmsService = new LlmsService()
  private currentPeriod: [Date, Date] = [new Date(), new Date()]
  private isEnabled = true
  private llmDataByMonth: { [key: string]: any } = {}

  get lastValues() {
    return this.getMonthAndYearFromDate(new Date(this.LLM.dateCreated || '2025-05-01'))
  }

  private getMonthAndYearFromDate(date: Date) {
    return { month: date.getMonth(), year: date.getFullYear() }
  }

  async mounted() {
    await this.refresh()
  }

  @Watch('currentPeriod')
  async refresh() {
    const [startDate, endDate] = this.currentPeriod
    const cursor = new Date(startDate)

    while (cursor <= endDate) {
      const key = `${cursor.getMonth()}-${cursor.getFullYear()}`
      if (!this.llmDataByMonth[key]) {
        const data = await this.llmService.getMetricsByLlmId(this.LLM.llmId, cursor)
        this.llmDataByMonth[key] = data[0] || {}
      }
      cursor.setMonth(cursor.getMonth() + 1)
    }
  }

  async setCurrentPeriod(period: [Date, Date]) {
    this.currentPeriod = period
  }

  private llmStatisticsConfig: AnalyticsTableConfig = {
    name: 'LLM Metrics Detailed',
    headerCells: ['Metric', 'Average', 'Maximum'],
    columnSettings: [
      { sortType: 'string' },
      { sortType: 'number' },
      { sortType: 'number' }
    ]
  }

  private setLLMStat(key: string, obj: any, stat: any) {
    const total = stat[`total${key}`] || 0;
    const max = stat[`max${key}`] || 0;
    obj.total[key] = (obj.total[key] || 0) + total
    obj.max[key] = Math.max(obj.max[key] || 0, max)
  }

  private getLLMStat(key: string, obj: any, count: number) {
    return [fromMillisecondsToSeconds(count > 0 ? (obj.total[key] / count) : 0), fromMillisecondsToSeconds(obj.max[key] || 0)]

  }

  get getLLMStats() {
    const obj = {total: {}, max: {}}

    let interactions = 0
    const [startDate, endDate] = this.currentPeriod
    const cursor = new Date(startDate)

    console.log(this.llmDataByMonth)

    while (cursor <= endDate) {
      const key = `${cursor.getMonth()}-${cursor.getFullYear()}`
      const daily = this.llmDataByMonth[key]?.dailyMetrics?.[formatLocaleDate(cursor)]
      if (!daily) {
        cursor.setDate(cursor.getDate() + 1)
        continue
      }

      this.setLLMStat("InputGuardrailsTime", obj, daily)
      this.setLLMStat("InputGuardrailsBlockingTime", obj, daily)
      this.setLLMStat("OutputGuardrailsTime", obj, daily)
      this.setLLMStat("OutputGuardrailsBlockingTime", obj, daily)
      this.setLLMStat("FirstSentenceTime", obj, daily)
      this.setLLMStat("RagFilterTime", obj, daily)
      this.setLLMStat("GetRagChainTime", obj, daily)
      this.setLLMStat("TotalChainTime", obj, daily)
      this.setLLMStat("LlmExecutionTotalTime", obj, daily)
      this.setLLMStat("ResponseTime", obj, daily)
      this.setLLMStat("LlmBlockTotalTime", obj, daily)

      interactions += daily.interactions || 0
      cursor.setDate(cursor.getDate() + 1)
    }

    return [
      ['Input guardrails time', ...this.getLLMStat("InputGuardrailsTime", obj, interactions)],
      ['Input guardrails time (blocking response)', ...this.getLLMStat("InputGuardrailsBlockingTime", obj, interactions)],
      ['Output guardrails time', ...this.getLLMStat("OutputGuardrailsTime", obj, interactions)],
      ['Output guardrails time (blocking response)', ...this.getLLMStat("OutputGuardrailsBlockingTime", obj, interactions)],
      ['First sentence generation time', ...this.getLLMStat("FirstSentenceTime", obj, interactions)],
      ['RAG filter time', ...this.getLLMStat("RagFilterTime", obj, interactions)],
      ['RAG chain time', ...this.getLLMStat("GetRagChainTime", obj, interactions)],
      ['Chain time', ...this.getLLMStat("TotalChainTime", obj, interactions)],
      ['LLM section time', ...this.getLLMStat("LlmExecutionTotalTime", obj, interactions)],
      ['LLM first content response time', ...this.getLLMStat("ResponseTime", obj, interactions)],
      ['LLM block total execution time', ...this.getLLMStat("LlmBlockTotalTime", obj, interactions)],
      { text: 'Model interactions', formula: interactions }
    ]
  }

}
</script>


<style scoped lang="scss">
@import '../../../../assets/scss/variables';

.description {
  font-size: 14px;
  margin-bottom: 15px;
}

.mt {
  margin-top: 20px;
}

.rect {
  &--highlight {
    color: white;
    width: 100%;
    height: auto;
    padding: 30px 30px 50px;
    background-color: $default-dark;
    border-radius: 10px;
  }
}
</style>
